import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { AppButton } from '@/components/AppButton'
import { useCreateKemCard } from '@/domain/wallet/useCases'
import { useCheckTransactionEligibility } from '@/hooks'
import { useKemCardStore } from '@/store'
import { useAppAnalytics } from '@/services/analytics'

interface CreateKemCardButtonProps {
  disabled?: boolean
  [key: string]: any // Allow other button props
}

export const CreateKemCardButton: React.FC<CreateKemCardButtonProps> = ({
  disabled,
  ...buttonProps
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: createKemCard, isPending: isLoadingKemCardCreation } =
    useCreateKemCard()
  const { checkTransactionEligibility } = useCheckTransactionEligibility({
    feature: 'card',
  })
  const setScreenState = useKemCardStore((state) => state.setScreenState)
  const { trackEvent, events } = useAppAnalytics()

  const redirectToFailureOrKemCardScreen = (hasError: boolean = false) => {
    if (hasError) {
      return navigate('/kem-card/creation-failure')
    }

    // Navigate back to main kem-card screen
    return navigate('/kem-card')
  }

  const handleSubmit = async () => {
    // Check eligibility before creating the card
    const isEligible = await checkTransactionEligibility()

    if (!isEligible) {
      return
    }

    try {
      await createKemCard()

      trackEvent(events.CARD_ACTIVATION_COMPLETED, {
        card_type: 'virtual', // Assuming virtual for now as it's the default flow
      })

      // Set screen state to PROCESSING immediately after successful submission
      // This ensures the user sees the processing screen right away
      // The actual status will be updated on the next poll or navigation
      setScreenState({
        screenType: 'PROCESSING',
        issueState: 'VALIDATION',
        isLoading: false,
      })

      // Show success message
      toast.success(
        t('translation:kemCard.cardCreated', 'Card created successfully!'),
      )

      redirectToFailureOrKemCardScreen()
    } catch (error) {
      console.error('Failed to create Kem Card:', error)

      // Show error message
      toast.error(
        t(
          'translation:kemCard.cardCreationFailed',
          'Failed to create card. Please try again.',
        ),
      )

      redirectToFailureOrKemCardScreen(true)
    }
  }

  return (
    <AppButton
      label={t('form:actions.confirm', 'Confirm')}
      onPress={handleSubmit}
      isLoading={isLoadingKemCardCreation}
      disabled={disabled || isLoadingKemCardCreation}
      type="primaryGradient"
      {...buttonProps}
    />
  )
}
